import axiosInstance from '../api';

// ── Framework resolution ──
// The AML frameworks the org is enrolled in, as decided by the backend (same rule it enforces on
// every /api/aml call). Empty means AML isn't enabled for the org. Every AML table is
// framework-scoped, so an org enrolled in more than one gets a switcher; every other function in
// this file requires a frameworkId argument and sends it as a query param.
export const resolveAmlFrameworks = () =>
  axiosInstance.get('/aml/frameworks').then((r) => (Array.isArray(r.data) ? r.data : [])
    .map((f) => ({
      id: f.id,
      shortName: f.shortName || f.name,
      displayName: f.displayName || f.name,
    })));

// Per-framework pass/fail counts — the same endpoint AuditPage.js reads its posture bar from
// ({ frameworkName, totalTests, passedTests, failedTests }), so the number shown here always
// agrees with what Audits already shows for the same framework.
export const getComplianceSummary = () =>
  axiosInstance.get('/audit/compliance-summary').then((r) => (Array.isArray(r.data) ? r.data : []));

// ── Overview + governance ──
export const getAmlOverview = (frameworkId) =>
  axiosInstance.get('/aml/overview', { params: { frameworkId } }).then((r) => r.data);
export const saveAmlGovernance = (frameworkId, body) =>
  axiosInstance.put('/aml/governance', body, { params: { frameworkId } }).then((r) => r.data);

// ── Risk assessment ──
export const listAmlRisks = (frameworkId) =>
  axiosInstance.get('/aml/risk-assessments', { params: { frameworkId } }).then((r) => r.data || []);
export const createAmlRisk = (frameworkId, body) =>
  axiosInstance.post('/aml/risk-assessments', body, { params: { frameworkId } }).then((r) => r.data);
export const updateAmlRisk = (frameworkId, id, body) =>
  axiosInstance.put(`/aml/risk-assessments/${id}`, body, { params: { frameworkId } }).then((r) => r.data);
export const linkAmlRiskControl = (frameworkId, id, controlId) =>
  axiosInstance.post(`/aml/risk-assessments/${id}/controls`, { controlId }, { params: { frameworkId } });
// The framework's controls (id, reference, name): the choices when linking a control to a risk.
export const listAmlControls = (frameworkId) =>
  axiosInstance.get('/aml/risk-assessments/controls', { params: { frameworkId } }).then((r) => r.data || []);
// Starter risk templates for the framework; `used` marks the ones already in this org's register.
export const listAmlRiskTemplates = (frameworkId) =>
  axiosInstance.get('/aml/risk-assessments/templates', { params: { frameworkId } }).then((r) => r.data || []);
export const createAmlRiskFromTemplate = (frameworkId, templateId) =>
  axiosInstance.post(`/aml/risk-assessments/from-template/${templateId}`, null, { params: { frameworkId } }).then((r) => r.data);
// `reference` is the minutes reference of the Comité / Consejo session; the review and approval
// steps require it, the first step ("mark prepared") does not.
export const advanceAmlRisk = (frameworkId, id, reference) =>
  axiosInstance.post(`/aml/risk-assessments/${id}/advance`, reference ? { reference } : {}, { params: { frameworkId } }).then((r) => r.data);
// FR-07: year-over-year version history + content-hash signing
export const reopenAmlRisk = (frameworkId, id) =>
  axiosInstance.post(`/aml/risk-assessments/${id}/reopen`, null, { params: { frameworkId } }).then((r) => r.data);
export const getAmlRiskHistory = (frameworkId, id) =>
  axiosInstance.get(`/aml/risk-assessments/${id}/history`, { params: { frameworkId } }).then((r) => r.data || []);
// Only a risk that was never approved can be deleted; the server refuses the rest.
export const deleteAmlRisk = (frameworkId, id) =>
  axiosInstance.delete(`/aml/risk-assessments/${id}`, { params: { frameworkId } });

// ── Customer segments ──
export const listCustomerSegments = (frameworkId) =>
  axiosInstance.get('/aml/segments', { params: { frameworkId } }).then((r) => r.data || []);
export const createCustomerSegment = (frameworkId, body) =>
  axiosInstance.post('/aml/segments', body, { params: { frameworkId } }).then((r) => r.data);
export const updateCustomerSegment = (frameworkId, id, body) =>
  axiosInstance.put(`/aml/segments/${id}`, body, { params: { frameworkId } }).then((r) => r.data);
// Refused by the server when the segment has quality samples on record.
export const deleteCustomerSegment = (frameworkId, id) =>
  axiosInstance.delete(`/aml/segments/${id}`, { params: { frameworkId } });

// ── Filings ──
export const listAmlFilings = (frameworkId) =>
  axiosInstance.get('/aml/filings', { params: { frameworkId } }).then((r) => r.data || []);
export const createAmlFiling = (frameworkId, body) =>
  axiosInstance.post('/aml/filings', body, { params: { frameworkId } }).then((r) => r.data);
// Correcting or deleting is for filings that are not submitted yet.
export const updateAmlFiling = (frameworkId, id, body) =>
  axiosInstance.put(`/aml/filings/${id}`, body, { params: { frameworkId } }).then((r) => r.data);
export const deleteAmlFiling = (frameworkId, id) =>
  axiosInstance.delete(`/aml/filings/${id}`, { params: { frameworkId } });
// Receipt is uploaded separately (direct file storage, not the generic Evidence module — see
// the backend migration comment on why) and required before submit() will succeed.
export const uploadAmlFilingReceipt = (frameworkId, id, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axiosInstance.post(`/aml/filings/${id}/receipt`, fd,
    { params: { frameworkId }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};
export const submitAmlFiling = (frameworkId, id) =>
  axiosInstance.post(`/aml/filings/${id}/submit`, null, { params: { frameworkId } }).then((r) => r.data);
export const updateAmlFilingStatus = (frameworkId, id, status) =>
  axiosInstance.put(`/aml/filings/${id}/status`, { status }, { params: { frameworkId } }).then((r) => r.data);
export const reconcileAmlFiling = (frameworkId, id, filedCount, monitoringEngineCount) =>
  axiosInstance.post(`/aml/filings/${id}/reconcile`, { filedCount, monitoringEngineCount }, { params: { frameworkId } }).then((r) => r.data);
// Case-management bridge: decided-cases CSV (case_ref, decision_date, report_type, due_date).
export const importDecidedCases = (frameworkId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axiosInstance.post('/aml/filings/import-decided-cases', fd,
    { params: { frameworkId }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

// ── Training (FR-12) ──
export const listAmlTraining = (frameworkId) =>
  axiosInstance.get('/aml/training', { params: { frameworkId } }).then((r) => r.data || []);
export const createAmlTraining = (frameworkId, body) =>
  axiosInstance.post('/aml/training', body, { params: { frameworkId } }).then((r) => r.data);
export const updateAmlTraining = (frameworkId, id, body) =>
  axiosInstance.put(`/aml/training/${id}`, body, { params: { frameworkId } }).then((r) => r.data);

// ── Quality sampling (FR-14) ──
export const listSegmentSamples = (frameworkId, segmentId) =>
  axiosInstance.get(`/aml/segments/${segmentId}/samples`, { params: { frameworkId } }).then((r) => r.data || []);
export const addSegmentSample = (frameworkId, segmentId, body) =>
  axiosInstance.post(`/aml/segments/${segmentId}/sample`, body, { params: { frameworkId } }).then((r) => r.data);
export const uploadSampleEvidence = (frameworkId, segmentId, sampleId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axiosInstance.post(`/aml/segments/${segmentId}/samples/${sampleId}/evidence`, fd,
    { params: { frameworkId }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};
// Downloads go through the API (bearer auth), then save as a browser download.
export const saveBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
export const downloadAmlFilingReceipt = (frameworkId, id) =>
  axiosInstance.get(`/aml/filings/${id}/receipt`, { params: { frameworkId }, responseType: 'blob' }).then((r) => r.data);
export const downloadSampleEvidence = (frameworkId, segmentId, sampleId) =>
  axiosInstance.get(`/aml/segments/${segmentId}/samples/${sampleId}/evidence`, { params: { frameworkId }, responseType: 'blob' }).then((r) => r.data);

// ── Core-banking connector (file import, Phase 1 — CSV only) ──
export const importCoreBankingFile = (frameworkId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axiosInstance.post('/aml/connectors/core-banking/import', fd,
    { params: { frameworkId }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};

// ── Vendor / correspondent due diligence (FR-17 remainder) — a lightweight recordkeeping
// surface deliberately separate from the generic Vendors catalog; see the backend migration's
// comment for why. ──
export const listVendorDueDiligence = (frameworkId) =>
  axiosInstance.get('/aml/vendor-due-diligence', { params: { frameworkId } }).then((r) => r.data || []);
export const createVendorDueDiligence = (frameworkId, body) =>
  axiosInstance.post('/aml/vendor-due-diligence', body, { params: { frameworkId } }).then((r) => r.data);
export const updateVendorDueDiligence = (frameworkId, id, body) =>
  axiosInstance.put(`/aml/vendor-due-diligence/${id}`, body, { params: { frameworkId } }).then((r) => r.data);
// Core-banking third-parties file (CSV: name, type, jurisdiction) — creates what's missing.
export const importThirdParties = (frameworkId, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return axiosInstance.post('/aml/vendor-due-diligence/import', fd,
    { params: { frameworkId }, headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
};
